import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  CircleDot,
  Clock,
  ExternalLink,
  Loader2,
  RotateCcw,
  Sparkles,
  Wand2,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

/**
 * R3 Phase 6A.7.1 — Generation provenance card on Run Detail.
 *
 * Renders only when GET /api/demo-runs/{run_id}/provenance returns 200
 * (i.e. the run was triggered by the AI auto-pipeline).  Surfaces the
 * AI brief, target URL, the originating generation_id + version, and
 * the three cost lines (generation LLM tokens + run TTS/narration +
 * grand total).
 *
 * The per-step success/failure detail lives inside the regular run
 * steps list; this card is the *header* — what the AI was asked to
 * do, and what the whole production cost.
 */
export default function ProvenanceCard({ runId, testid = "provenance-card" }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["run-provenance", runId],
    queryFn: async () => (await api.get(`/demo-runs/${runId}/provenance`)).data,
    retry: false,
    staleTime: 30_000,
  });

  // 404 means "not AI-generated" — render nothing.  Anything else,
  // bubble it as a small banner so the user knows something's off.
  if (error) {
    const status = error?.response?.status;
    if (status === 404) return null;
    return (
      <div
        className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700"
        data-testid={`${testid}-error`}
      >
        Couldn't load AI provenance: {formatApiError(error)}
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div
        className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[12px] text-slate-400"
        data-testid={`${testid}-loading`}
      >
        Loading generation provenance…
      </div>
    );
  }

  const ratio =
    data.steps_count > 0
      ? data.success_count / data.steps_count
      : 0;
  const ratioPct = Math.round(ratio * 100);
  const reasonMeta = TERMINATED_REASON_META[data.terminated_reason] || {
    label: data.terminated_reason || "unknown",
    tone: "neutral",
  };

  return (
    <section
      className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4"
      data-testid={testid}
      data-generation-id={data.generation_id}
    >
      <header className="mb-3 flex items-center gap-2">
        <Wand2 size={15} className="text-indigo-600" />
        <h3 className="text-sm font-semibold tracking-tight text-slate-900">
          Generation provenance
        </h3>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 font-mono text-[10px] text-slate-500 ring-1 ring-slate-200">
          <CircleDot size={9} className="text-indigo-500" />
          {data.generation_id.slice(0, 8)} · v{data.version}
        </span>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
        {/* Brief + target URL */}
        <div className="sm:col-span-7 space-y-2">
          <Field label="AI brief">
            <p
              className="text-sm leading-snug text-slate-800"
              data-testid={`${testid}-brief`}
            >
              {data.ai_brief || "—"}
            </p>
          </Field>
          <Field label="Target URL">
            <a
              href={data.target_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-mono text-indigo-700 hover:underline"
              data-testid={`${testid}-target`}
            >
              {data.target_url}
              <ExternalLink size={10} />
            </a>
          </Field>
        </div>

        {/* Cost panel */}
        <div className="sm:col-span-5 rounded-lg border border-slate-200 bg-white p-3 text-[12px]">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Cost
          </div>
          <CostLine
            label="Step generation (LLM)"
            value={data.generation_cost_usd}
            testid={`${testid}-gen-cost`}
          />
          <CostLine
            label="Run (narration + TTS)"
            value={data.run_cost_usd}
            testid={`${testid}-run-cost`}
          />
          <div className="mt-1 border-t border-slate-200 pt-1.5">
            <CostLine
              label="Grand total"
              value={data.grand_total_usd}
              bold
              testid={`${testid}-grand-total`}
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
        <ReasonPill meta={reasonMeta} testid={`${testid}-reason`} />
        <SuccessPill
          ratio={ratio}
          ratioPct={ratioPct}
          success={data.success_count}
          steps={data.steps_count}
          testid={`${testid}-success-ratio`}
        />
        <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-slate-500 ring-1 ring-slate-200">
          <Clock size={10} />
          {Math.round(Number(data.duration_seconds) || 0)}s gen time
        </span>
        {data.auto_run_on_success && (
          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-700 ring-1 ring-indigo-200">
            <Sparkles size={10} />
            Auto-run was on
          </span>
        )}
      </div>
    </section>
  );
}

const TERMINATED_REASON_META = {
  complete: { label: "Generation complete", tone: "success" },
  max_steps: { label: "Step cap reached", tone: "warning" },
  max_cost: { label: "Cost cap reached", tone: "warning" },
  timeout: { label: "Time cap reached", tone: "warning" },
  cancelled: { label: "Cancelled", tone: "warning" },
  auth_failed: { label: "Auth failed", tone: "error" },
  target_unreachable: { label: "Target unreachable", tone: "error" },
  invalid_response: { label: "Model returned invalid JSON", tone: "error" },
  agent_stuck_no_op: { label: "No-op loop", tone: "warning" },
  llm_failed: { label: "LLM call failed", tone: "error" },
  playwright_crash: { label: "Browser crashed", tone: "error" },
};

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function CostLine({ label, value, bold = false, testid }) {
  return (
    <div className={`flex items-center justify-between py-0.5 ${bold ? "font-semibold text-slate-900" : "text-slate-600"}`}>
      <span>{label}</span>
      <span className="font-mono" data-testid={testid}>
        ${Number(value || 0).toFixed(4)}
      </span>
    </div>
  );
}

function ReasonPill({ meta, testid }) {
  const cls =
    meta.tone === "success"
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
      : meta.tone === "error"
      ? "bg-rose-50 text-rose-700 ring-rose-200"
      : meta.tone === "warning"
      ? "bg-amber-50 text-amber-800 ring-amber-200"
      : "bg-slate-50 text-slate-700 ring-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 ${cls}`}
      data-testid={testid}
    >
      {meta.label}
    </span>
  );
}

function SuccessPill({ ratio, ratioPct, success, steps, testid }) {
  const cls =
    ratio >= 0.8
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
      : ratio >= 0.5
      ? "bg-amber-50 text-amber-800 ring-amber-200"
      : "bg-rose-50 text-rose-700 ring-rose-200";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono ring-1 ${cls}`}
      data-testid={testid}
    >
      {success}/{steps} steps ({ratioPct}%)
    </span>
  );
}

// ---------------------------------------------------------------------------
// R3 Phase 6A.7.1 — Replay-step modal + control hooks
//
// Hooks API exported from this module so the parent RunDetailPage can
// drop a single "Replay" button onto each step row without rewriting
// the existing step-row component.
// ---------------------------------------------------------------------------

export function useReplayStepModal() {
  const [target, setTarget] = useState(null); // { runId, stepId, stepName }
  return {
    target,
    open: ({ runId, stepId, stepName }) => setTarget({ runId, stepId, stepName }),
    close: () => setTarget(null),
  };
}

export function ReplayStepDialog({ control, testid = "replay-step-dialog" }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState(null); // { run_id, step_id, original_status, replay_status, duration_ms, error_message }

  const mutation = useMutation({
    mutationFn: async ({ runId, stepId }) => {
      const { data } = await api.post(
        `/demo-runs/${runId}/steps/${stepId}/replay`,
      );
      return data;
    },
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ["audit-events"] });
    },
    onError: (err) => {
      toast.error(formatApiError(err));
    },
  });

  const open = !!control.target;
  function onOpenChange(o) {
    if (!o && !mutation.isPending) {
      control.close();
      setResult(null);
      mutation.reset();
    }
  }

  // Replay-result UI states.
  const status = result?.replay_status;
  const original = result?.original_status;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid={testid}
        data-replay-status={status || (mutation.isPending ? "running" : "idle")}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw size={15} className="text-indigo-600" />
            Replay step in isolation
          </DialogTitle>
          <DialogDescription>
            Re-runs this step alone in a fresh headless Chromium. Useful for
            verifying whether a failure is deterministic vs. flaky.
            Mid-workflow steps that need login state may fail here — that's
            diagnostic too.
          </DialogDescription>
        </DialogHeader>

        {control.target && (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-[12px] text-slate-700">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">
              Step
            </div>
            <div className="mt-0.5 font-medium text-slate-900" data-testid={`${testid}-step-name`}>
              {control.target.stepName || control.target.stepId}
            </div>
          </div>
        )}

        {!result && (
          <p className="text-[12px] text-slate-500">
            Click <strong>Run replay</strong> to start. This typically takes
            10-30 seconds. The original run's step status is not modified.
          </p>
        )}

        {mutation.isPending && (
          <div
            className="flex items-center gap-2 rounded-md bg-indigo-50 px-3 py-2 text-[12px] text-indigo-800"
            data-testid={`${testid}-running`}
          >
            <Loader2 size={13} className="animate-spin" />
            Running replay…
          </div>
        )}

        {result && (
          <div className="space-y-2">
            <ReplayOutcomeBanner status={status} testid={testid} />
            <div className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 px-3 py-2 text-[12px]">
              <Pair label="Original status" value={original || "—"} testid={`${testid}-original-status`} />
              <Pair label="Replay status" value={status || "—"} testid={`${testid}-replay-status`} />
              <Pair label="Duration" value={`${Math.round(result.duration_ms || 0)}ms`} testid={`${testid}-duration`} />
              <Pair label="Replay run id" value={(result.new_run_id || "—").slice(0, 20)} testid={`${testid}-new-run-id`} />
            </div>
            {result.error_message && (
              <pre
                className="max-h-32 overflow-y-auto rounded-md bg-rose-50 px-3 py-2 text-[11px] text-rose-800 ring-1 ring-rose-200"
                data-testid={`${testid}-error-message`}
              >
                {result.error_message}
              </pre>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {!result ? (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
                data-testid={`${testid}-cancel`}
              >
                Close
              </Button>
              <Button
                onClick={() =>
                  control.target && mutation.mutate(control.target)
                }
                disabled={mutation.isPending}
                className="gap-1.5 bg-indigo-600 hover:bg-indigo-700"
                data-testid={`${testid}-go`}
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    Running…
                  </>
                ) : (
                  <>
                    <RotateCcw size={13} />
                    Run replay
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid={`${testid}-done`}
            >
              Done
            </Button>
          )}
          {result?.new_run_id && (
            <Link
              to={`/runs/${result.new_run_id}`}
              className="inline-flex items-center gap-1 text-[11px] text-indigo-700 hover:underline"
              data-testid={`${testid}-view-replay-run`}
            >
              View replay run
              <ExternalLink size={9} />
            </Link>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Pair({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-slate-900" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function ReplayOutcomeBanner({ status, testid }) {
  const cls =
    status === "completed"
      ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
      : status === "skipped"
      ? "bg-amber-50 text-amber-800 ring-amber-200"
      : "bg-rose-50 text-rose-800 ring-rose-200";
  const Icon =
    status === "completed"
      ? CheckCircle2
      : status === "skipped"
      ? AlertTriangle
      : XCircle;
  const label =
    status === "completed"
      ? "Replay passed"
      : status === "skipped"
      ? "Replay skipped"
      : "Replay failed";
  return (
    <div
      className={`flex items-center gap-2 rounded-md px-3 py-2 text-[12px] font-medium ring-1 ${cls}`}
      data-testid={`${testid}-banner`}
    >
      <Icon size={14} />
      {label}
    </div>
  );
}
